import React from 'react';
import { Helmet } from 'react-helmet-async';
import './Privacy.css';

export default function Privacy() {
  return (
    <div>
      <Helmet>
        <title>Privacy Policy | Echo Hill</title>
        <meta name="description" content="Echo Hill privacy policy — how we collect and use data on echohillband.com." />
      </Helmet>
      <div className="section-container">
        <h1 className="section-title">Privacy</h1>
        <div className="section-rule" />

        <div className="privacy-body">

          <p className="privacy-effective">Effective Date: May 25, 2026</p>

          <section>
            <h2>Overview</h2>
            <p>Echo Hill ("we", "us", "our") operates the website echohillband.com. We respect your privacy and are committed to being transparent about how we collect and use information when you visit our site. This policy explains what data is collected, how it is used, and your rights as a visitor.</p>
          </section>

          <section>
            <h2>Information We Collect</h2>
            <p>We do not directly collect personal information such as your name, email address, or payment information through this website. However, third-party services embedded in or connected to this site may collect data as described below.</p>
            <h3>Automatically Collected Data via Google Analytics</h3>
            <p>We use Google Analytics to understand how visitors interact with our site. Google Analytics automatically collects:</p>
            <ul>
              <li>Pages visited and time spent on each page</li>
              <li>General geographic location (country, region, city)</li>
              <li>Browser type and operating system</li>
              <li>Device type (desktop, mobile, tablet)</li>
              <li>Referring website or search engine</li>
              <li>Anonymized IP address</li>
            </ul>
            <p>This data is aggregated and does not identify you personally. It is used solely to help us understand our audience and improve the site.</p>
          </section>

          <section>
            <h2>Cookies</h2>
            <p>Google Analytics uses cookies — small text files stored in your browser — to track site usage over time. These cookies do not contain personal information and are not used for advertising purposes.</p>
            <p>You can opt out of Google Analytics tracking at any time by installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>.</p>
            <p>You can also manage or delete cookies through your browser settings.</p>
          </section>

          <section>
            <h2>Global Privacy Control (GPC)</h2>
            <p>We respect the Global Privacy Control signal. If your browser or device transmits a GPC signal indicating that you do not want your personal information sold or shared, we will treat that signal as a valid opt-out request under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA). Because we do not sell or share personal information for cross-context behavioral advertising, honoring this signal has no practical effect on your experience of this site.</p>
          </section>

          <section>
            <h2>Third Party Services</h2>
            <p>This site embeds or links to the following third-party services, each with their own privacy practices:</p>
            <ul>
              <li><strong>Google Analytics</strong> — analytics and traffic measurement. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
              <li><strong>Bandcamp</strong> — music player and merch store embedded on this site. <a href="https://bandcamp.com/privacy" target="_blank" rel="noopener noreferrer">Bandcamp Privacy Policy</a></li>
              <li><strong>YouTube</strong> — video embeds. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google/YouTube Privacy Policy</a></li>
              <li><strong>Bandsintown</strong> — live show listings widget. <a href="https://corp.bandsintown.com/privacy" target="_blank" rel="noopener noreferrer">Bandsintown Privacy Policy</a></li>
              <li><strong>Spotify, Apple Music, Amazon Music</strong> — external streaming links. Their respective privacy policies apply when you visit those platforms.</li>
            </ul>
            <p>We do not control how these third parties collect or use your data. We encourage you to review their privacy policies directly.</p>
          </section>

          <section>
            <h2>California Residents — CCPA/CPRA Rights</h2>
            <p>If you are a California resident, you have the following rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):</p>
            <ul>
              <li><strong>Right to Know</strong> — you may request information about what personal data has been collected about you</li>
              <li><strong>Right to Delete</strong> — you may request deletion of personal data we hold about you</li>
              <li><strong>Right to Correct</strong> — you may request correction of inaccurate personal data</li>
              <li><strong>Right to Opt Out of Sale or Sharing</strong> — we do not sell or share personal information for cross-context behavioral advertising. There is nothing to opt out of in that regard</li>
              <li><strong>Right to Limit Use of Sensitive Personal Information</strong> — we do not collect sensitive personal information</li>
              <li><strong>Right to Non-Discrimination</strong> — exercising your privacy rights will not result in any difference in service</li>
              <li><strong>Right to GPC Opt-Out</strong> — we honor Global Privacy Control signals as described above</li>
            </ul>
            <p>To exercise any of these rights, contact us at <a href="mailto:echohillinfo@gmail.com">echohillinfo@gmail.com</a>. Because we do not directly collect personal data, most requests will be directed to the relevant third-party service (primarily Google Analytics).</p>
          </section>

          <section>
            <h2>European Visitors — GDPR</h2>
            <p>While Echo Hill is a US-based band primarily serving a US audience, we acknowledge that visitors from the European Union or European Economic Area may access this site. If you are located in the EU or EEA, you have rights under the General Data Protection Regulation (GDPR) including the right to access, rectify, erase, restrict processing of, and port your personal data, as well as the right to object to processing.</p>
            <p>The primary data processor on this site is Google Analytics. Google acts as a data processor on our behalf and processes data in accordance with their GDPR commitments. For data access or deletion requests related to Google Analytics data, please contact us at <a href="mailto:echohillinfo@gmail.com">echohillinfo@gmail.com</a> or visit <a href="https://myaccount.google.com/data-and-privacy" target="_blank" rel="noopener noreferrer">Google's privacy controls</a> directly.</p>
            <p>We do not have a formal EU representative or Data Protection Officer as we do not meet the threshold for mandatory appointment under GDPR. We will make reasonable efforts to respond to EU data subject requests within 30 days.</p>
          </section>

          <section>
            <h2>Data Retention</h2>
            <p>We do not store personal data on our servers. Google Analytics data is retained per Google's standard retention settings, which can be adjusted in your Google account settings.</p>
          </section>

          <section>
            <h2>Children's Privacy</h2>
            <p>This site is not directed at children under the age of 13 and we do not knowingly collect data from children. If you believe a child under 13 has provided personal information through this site, please contact us at <a href="mailto:echohillinfo@gmail.com">echohillinfo@gmail.com</a> and we will take steps to delete it.</p>
          </section>

          <section>
            <h2>Changes to This Policy</h2>
            <p>We may update this policy from time to time. Changes will be posted on this page with an updated effective date. Continued use of the site after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2>Contact</h2>
            <p>For privacy-related questions or requests:</p>
            <p>Echo Hill<br /><a href="mailto:echohillinfo@gmail.com">echohillinfo@gmail.com</a></p>
          </section>

        </div>
      </div>
    </div>
  );
}
